/**
 * Core: Canvas imports
 * -----------------------------------------------------------------------------
 * Import async components, global components, and Vuex stores for use in
 * CanvasApp Vue instance.
 *
 */
/* eslint-disable max-len, no-inline-comments, quote-props */
import { defineAsyncComponent } from 'vue'

/**
 * Get async components and imports.
 * - Must be manually updated with new async components (components/async/).
 * - Previously used a dynamic import statement which caused infinite build
 *   issue and generated unused files.
 * - Only parent components should be imported.
 * - Do not import components that are only ever used as a child component.
 * - Do not import async sub-components.
 * @returns {Object}
 */
export function getAsyncComponents() {
  return {
    // canvas-async-object-start
    'accordion-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.accordion-section' */'~async/accordion-section/accordion-section') }),
    'add-to-cart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.add-to-cart' */'~async/product-card/add-to-cart/add-to-cart') }),
    'breadcrumbs': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.breadcrumbs' */'~async/breadcrumbs/breadcrumbs') }),
    'cart-drawer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cart-drawer' */'~async/cart-drawer/cart-drawer') }),
    'collection-usp': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.collection-usp' */'~async/collection-usp/collection-usp') }),
    'colour-swatch': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.colour-swatch' */'~async/product-card/colour-swatch/colour-swatch') }),
    'contact-form': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.contact-form' */'~async/contact-form/contact-form') }),
    'country-selector': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.country-selector' */'~async/country-selector/country-selector') }),
    'country-selector-us': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.country-selector-us' */'~async/country-selector-us/country-selector-us') }),
    'customers-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-account' */'~async/customers/account/customers-account') }),
    'customers-activate-account': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-activate-account' */'~async/customers/activate-account/customers-activate-account') }),
    'customers-addresses': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-addresses' */'~async/customers/addresses/customers-addresses') }),
    'customers-login': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-login' */'~async/customers/login/customers-login') }),
    'customers-order': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-order' */'~async/customers/order/customers-order') }),
    'customers-orders': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-orders' */'~async/customers/orders/customers-orders') }),
    'customers-register': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-register' */'~async/customers/register/customers-register') }),
    'customers-reset-password': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.customers-reset-password' */'~async/customers/reset-password/customers-reset-password') }),
    'doorway-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.doorway-section' */'~async/doorway-section/doorway-section') }),
    'featured-products': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.featured-products' */'~async/featured-products/featured-products') }),
    'full-image-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.full-image-banner' */'~async/full-image-banner/full-image-banner') }),
    'html-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.html-section' */'~async/html-section/html-section') }),
    'klarna-messaging': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.klarna-messaging' */'~async/klarna-messaging/klarna-messaging') }),
    'main-404': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-404' */'~async/main-404/main-404') }),
    'main-article': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-article' */'~async/main-article/main-article') }),
    'main-blog': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-blog' */'~async/main-blog/main-blog') }),
    'main-cart': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-cart' */'~async/main-cart/main-cart') }),
    'main-collection': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-collection' */'~async/main-collection/main-collection') }),
    'main-list-collections': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-list-collections' */'~async/main-list-collections/main-list-collections') }),
    'main-page': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-page' */'~async/main-page/main-page') }),
    'main-product': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-product' */'~async/main-product/main-product') }),
    'main-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.main-search' */'~async/main-search/main-search') }),
    'partners-section': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.partners-section' */'~async/partners-section/partners-section') }),
    'pre-lander': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.pre-lander' */'~async/pre-lander/pre-lander') }),
    'predictive-search': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.predictive-search' */'~async/predictive-search/predictive-search') }),
    'product-accordion': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-accordion' */'~async/product-accordion/product-accordion') }),
    'product-details': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-details' */'~async/product-details/product-details') }),
    'product-features': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-features' */'~async/product-features/product-features') }),
    'product-recommendations': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-recommendations' */'~async/product-recommendations/product-recommendations') }),
    'product-usp': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.product-usp' */'~async/product-usp/product-usp') }),
    'promo-banner': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.promo-banner' */'~async/promo-banner/promo-banner') }),
    'redirect-modal': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.redirect-modal' */'~async/redirect-modal/redirect-modal') }),
    'returns-form': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.returns-form' */'~async/returns-form/returns-form') }),
    'site-footer': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.site-footer' */'~async/site-footer-group/site-footer/site-footer') }),
    'size-finder': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.size-finder' */'~async/size-finder/size-finder') }),
    'stories-block': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.stories-block' */'~async/stories-block/stories-block') }),
    'text-media': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.text-media' */'~async/text-media/text-media') }),
    'transition-height': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.transition-height' */'~async/transition-height/transition-height') }),
    'upsell-carousel': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.upsell-carousel' */'~async/upsell-carousel/upsell-carousel') }),
    'verify-pass': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.verify-pass' */'~async/verify-pass/verify-pass') }),
    // 'cnvs-featured-products': defineAsyncComponent({ loader: () => import(/* webpackChunkName: 'component.cnvs-featured-products' */'~async/cnvs-featured-products/cnvs-featured-products') }),
    // canvas-async-object-end
  }
}

/**
 * Import global components here.
 */
// canvas-global-import-start
import Accessibility from '~global/core/accessibility'
import AccordionComponent from '~global/accordion-component/accordion-component'
import ArtDirection from '~global/images/art-direction'
import Btn from '~global/btn/btn'
import Index from '~global/core/index'
import Navigation from '~global/site-header-group/navigation/navigation'
import Overlay from '~global/overlay/overlay'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import SiteHeader from '~global/site-header-group/site-header/site-header'
import SiteHeaderGroup from '~global/site-header-group/site-header-group'
// canvas-global-import-end

/**
 * Get global components and imports.
 * - Must be manually updated with new global components (components/global).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 * @returns {Object}
 */
export function getGlobalComponents() {
  return {
    // canvas-global-object-start
    'accessibility': Accessibility,
    'accordion-component': AccordionComponent,
    'art-direction': ArtDirection,
    'btn': Btn,
    'index': Index,
    'navigation': Navigation,
    'overlay': Overlay,
    'product-prices': ProductPrices,
    'responsive-image': ResponsiveImage,
    'site-header': SiteHeader,
    'site-header-group': SiteHeaderGroup,
    // canvas-global-object-end
  }
}

/**
 * Import Vuex stores here.
 * - Import with Store suffix to avoid conflicts with global component imports.
 */
// canvas-stores-import-start
import AccessibilityStore from '~stores/accessibility'
import CartStore from '~stores/cart'
import ComponentsStore from '~stores/components'
import IndexStore from '~stores/index'
import OverlaysStore from '~stores/overlays'
import PredictiveSearchStore from '~stores/search'
// canvas-stores-import-end

/**
 * Get Vuex stores and imports.
 * - Must be manually updated with new Vuex stores (stores/).
 * - Previously used require.context but this caused infinite build issue.
 * - All property keys are wrapped in quotations for consistency.
 */
export function getVuexStores() {
  return {
    // canvas-stores-object-start
    'accessibility': AccessibilityStore,
    'cart': CartStore,
    'components': ComponentsStore,
    'index': IndexStore,
    'overlays': OverlaysStore,
    'search': PredictiveSearchStore,
    // canvas-stores-object-end
  }
}
